import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Eye,
  EyeOff,
  Folder,
  Globe,
  KeyRound,
  Lock,
  Sparkles,
  Star,
  User,
  X as CloseIcon,
} from 'lucide-react';
import { Button } from './Button';
import { useAuth } from '../contexts/AuthContext';
import { SecurityAuthModal } from './SecurityAuthModal';

const EMPTY_FORM = {
  title: '',
  url: '',
  username: '',
  password: '',
  categoryId: '',
  category: '',
  notes: '',
  totpSecret: '',
  customFields: '',
  isFavorite: false,
};

const createForm = (item, categories = []) => {
  if (!item) {
    const defaultCat = categories.find((c) => /login|password/i.test(c.name)) || categories[0];
    return {
      ...EMPTY_FORM,
      categoryId: defaultCat?.id || '',
      category: defaultCat?.name || '',
    };
  }

  const plaintext = item.plaintext || {};
  const catId = item.category_id || (categories.find((c) => c.name === (plaintext.category || item.category_name))?.id) || '';
  const catName = plaintext.category || item.category_name || (categories.find((c) => c.id === catId)?.name) || '';

  return {
    ...EMPTY_FORM,
    title: plaintext.title || '',
    url: plaintext.url || '',
    username: plaintext.username || '',
    password: plaintext.password || '',
    categoryId: catId,
    category: catName,
    notes: plaintext.notes || '',
    totpSecret: plaintext.totpSecret || '',
    customFields: plaintext.customFields ? JSON.stringify(plaintext.customFields, null, 2) : '',
    isFavorite: Boolean(item.is_favorite),
  };
};

const generatePassword = () => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*_-+=';
  const values = new Uint32Array(24);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => alphabet[value % alphabet.length]).join('');
};

const STRENGTH_CONFIG = [
  { label: 'Very Weak', color: '#ef4444' },
  { label: 'Weak', color: '#f97316' },
  { label: 'Fair', color: '#eab308' },
  { label: 'Good', color: '#10b981' },
  { label: 'Strong', color: '#06b6d4' },
  { label: 'Excellent', color: '#3b82f6' },
];

const getStrength = (password) => {
  if (!password) {
    return { label: 'Not set', score: 0, color: 'var(--text-secondary)' };
  }

  let score = 0;
  if (password.length >= 10) score += 1;
  if (password.length >= 16) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/\d/.test(password)) score += 1;
  if (/[^A-Za-z0-9]/.test(password)) score += 1;

  const boundedScore = Math.min(Math.max(score, 1), 5);
  const config = STRENGTH_CONFIG[boundedScore];
  return { label: config.label, score: boundedScore, color: config.color };
};

export const VaultItemModal = ({ open, item, categories = [], onClose, onSave, loading, error }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [formError, setFormError] = useState('');
  const { isSensitiveAuthFresh } = useAuth();

  useEffect(() => {
    if (open) {
      setForm(createForm(item, categories));
      setShowPassword(false);
      setIsAuthOpen(false);
      setFormError('');
    }
  }, [open, item, categories]);

  // Lock body scroll on mobile when drawer is open
  useEffect(() => {
    if (open) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  // Handle ESC key to close drawer
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isAuthOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, isAuthOpen, onClose]);

  const handleTogglePassword = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (showPassword) {
      setShowPassword(false);
      return;
    }
    if (item?.id && (!isSensitiveAuthFresh || !isSensitiveAuthFresh())) {
      setIsAuthOpen(true);
      return;
    }
    setShowPassword(true);
  }, [showPassword, item?.id, isSensitiveAuthFresh]);

  const strength = useMemo(() => getStrength(form.password), [form.password]);

  if (!open) {
    return null;
  }

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;
    const category = categories.find((entry) => entry.id === categoryId);
    setForm((current) => ({ ...current, categoryId, category: category?.name || '' }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    if (!form.title.trim()) {
      setFormError('Name is required.');
      return;
    }

    if (!form.password) {
      setFormError('Password is required.');
      return;
    }

    let customFields;
    if (form.customFields.trim()) {
      try {
        customFields = JSON.parse(form.customFields);
      } catch {
        setFormError('Custom fields must be valid JSON.');
        return;
      }
    }

    await onSave({
      title: form.title.trim(),
      url: form.url.trim(),
      username: form.username.trim(),
      password: form.password,
      category: form.category,
      notes: form.notes,
      totpSecret: form.totpSecret.trim(),
      customFields,
      isFavorite: form.isFavorite,
      categoryId: form.categoryId || null,
    });
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      {/* Backdrop overlay */}
      <div
        className="card-drawer-overlay animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* iOS-Style Right Sidebar Drawer */}
      <aside
        className="card-drawer vault-drawer ios-vault-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="vault-drawer-title"
      >
        {/* Header */}
        <div className="card-drawer-header">
          <div className="card-drawer-header-left">
            <div
              className="card-drawer-icon-badge"
              style={{
                background: 'linear-gradient(135deg, #0284c7, #0369a1)',
                color: '#ffffff',
              }}
            >
              <KeyRound size={18} />
            </div>
            <div className="card-drawer-titles">
              <h2 id="vault-drawer-title" className="card-drawer-title">
                {item ? 'Edit Password' : 'New Password'}
              </h2>
              <p className="card-drawer-subtitle">
                End-to-end encrypted on device
              </p>
            </div>
          </div>
          <button
            type="button"
            className="card-drawer-close modal-close-btn icon-button"
            onClick={onClose}
            aria-label="Close drawer"
          >
            <CloseIcon size={18} />
          </button>
        </div>

        {/* Scrollable iOS Inset Group Body */}
        <form
          id="vault-item-form"
          className="card-drawer-body ios-vault-form"
          onSubmit={handleSubmit}
        >
          {/* Section 1: Account Information */}
          <div className="ios-group">
            <div className="ios-group-label">Account Information</div>
            <div className="ios-group-card">
              {/* Item Name */}
              <div className="ios-row-field">
                <div
                  className="ios-field-icon"
                  style={{ background: 'rgba(2, 132, 199, 0.15)', color: '#0284c7' }}
                >
                  <KeyRound size={14} />
                </div>
                <span className="ios-field-label">Name</span>
                <input
                  className="ios-field-input"
                  value={form.title}
                  onChange={(e) => updateField('title', e.target.value)}
                  placeholder="e.g. Google, GitHub"
                  autoFocus
                />
              </div>

              <div className="ios-row-divider" />

              {/* Website URL */}
              <div className="ios-row-field">
                <div
                  className="ios-field-icon"
                  style={{ background: 'rgba(16, 185, 129, 0.15)', color: '#10b981' }}
                >
                  <Globe size={14} />
                </div>
                <span className="ios-field-label">Website</span>
                <input
                  className="ios-field-input"
                  type="url"
                  value={form.url}
                  onChange={(e) => updateField('url', e.target.value)}
                  placeholder="https://example.com"
                  inputMode="url"
                />
              </div>

              <div className="ios-row-divider" />

              {/* Username */}
              <div className="ios-row-field">
                <div
                  className="ios-field-icon"
                  style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}
                >
                  <User size={14} />
                </div>
                <span className="ios-field-label">Username</span>
                <input
                  className="ios-field-input"
                  value={form.username}
                  onChange={(e) => updateField('username', e.target.value)}
                  placeholder="email or username"
                  autoComplete="username"
                />
              </div>

              <div className="ios-row-divider" />

              {/* Category */}
              <div className="ios-row-field">
                <div
                  className="ios-field-icon"
                  style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}
                >
                  <Folder size={14} />
                </div>
                <span className="ios-field-label">Category</span>
                <select
                  className="ios-field-input ios-field-select"
                  value={form.categoryId}
                  onChange={handleCategoryChange}
                >
                  {(!form.categoryId || !categories.some((cat) => cat.id === form.categoryId)) && (
                    <option value="" disabled hidden>
                      Select Category
                    </option>
                  )}
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Security & Credentials */}
          <div className="ios-group">
            <div className="ios-group-label">Security &amp; Credentials</div>
            <div className="ios-group-card">
              {/* Password Row */}
              <div className="ios-row-field ios-password-row">
                <div
                  className="ios-field-icon"
                  style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}
                >
                  <Lock size={14} />
                </div>
                <span className="ios-field-label">Password</span>
                <div className="ios-password-controls">
                  <input
                    className="ios-field-input"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    placeholder="Required"
                    autoComplete={item ? 'current-password' : 'new-password'}
                    style={{ fontFamily: showPassword ? 'inherit' : 'monospace', letterSpacing: showPassword ? 'normal' : '1.5px' }}
                  />
                  <button
                    type="button"
                    className="ios-icon-action"
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={handleTogglePassword}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                  <button
                    type="button"
                    className="ios-icon-action ios-generate-btn"
                    onClick={() => updateField('password', generatePassword())}
                    title="Generate secure password"
                    aria-label="Generate secure password"
                  >
                    <Sparkles size={14} />
                  </button>
                </div>
              </div>

              {/* Password Strength Indicator */}
              {form.password && (
                <div className="ios-strength-wrap">
                  <div className="ios-strength-bars">
                    {[1, 2, 3, 4, 5].map((seg) => (
                      <div
                        key={seg}
                        className="ios-strength-bar-seg"
                        style={{
                          backgroundColor:
                            seg <= strength.score ? strength.color : 'var(--border-color)',
                        }}
                      />
                    ))}
                  </div>
                  <span
                    className="ios-strength-label"
                    style={{ color: strength.color }}
                  >
                    {strength.label}
                  </span>
                </div>
              )}

              <div className="ios-row-divider" />

              {/* TOTP Secret */}
              <div className="ios-row-field">
                <div
                  className="ios-field-icon"
                  style={{ background: 'rgba(100, 116, 139, 0.15)', color: '#64748b' }}
                >
                  <KeyRound size={14} />
                </div>
                <span className="ios-field-label">TOTP Key</span>
                <input
                  className="ios-field-input"
                  value={form.totpSecret}
                  onChange={(e) => updateField('totpSecret', e.target.value)}
                  placeholder="Authenticator secret (optional)"
                  autoComplete="off"
                />
              </div>
            </div>
          </div>

          {/* Section 3: Notes & Settings */}
          <div className="ios-group">
            <div className="ios-group-label">Notes &amp; Settings</div>
            <div className="ios-group-card">
              {/* Notes */}
              <div className="ios-row-field ios-notes-row">
                <textarea
                  className="ios-field-input ios-notes-area"
                  value={form.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  placeholder="Add notes, security questions, or pin codes…"
                  rows={2}
                />
              </div>

              <div className="ios-row-divider" />

              {/* Favorites Toggle Switch */}
              <div className="ios-row-field">
                <div
                  className="ios-field-icon"
                  style={{ background: 'rgba(234, 179, 8, 0.15)', color: '#eab308' }}
                >
                  <Star size={14} />
                </div>
                <span className="ios-field-label ios-label-full">Favorite</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={form.isFavorite}
                  className={`ios-toggle-switch ${form.isFavorite ? 'on' : ''}`}
                  onClick={() => updateField('isFavorite', !form.isFavorite)}
                  aria-label="Toggle favorite status"
                >
                  <span className="ios-toggle-thumb" />
                </button>
              </div>
            </div>
          </div>

          {/* Section 4: Advanced (JSON Custom Fields) */}
          <div className="ios-group">
            <details className="ios-advanced-details">
              <summary>Custom Fields (JSON)</summary>
              <div className="ios-group-card" style={{ marginTop: '8px' }}>
                <div className="ios-row-field">
                  <textarea
                    className="ios-field-input ios-notes-area ios-code-area"
                    value={form.customFields}
                    onChange={(e) => updateField('customFields', e.target.value)}
                    placeholder={'{\n  "recoveryEmail": "backup@example.com"\n}'}
                    rows={3}
                    spellCheck="false"
                  />
                </div>
              </div>
            </details>
          </div>

          {/* Error Message */}
          {(formError || error) && (
            <div className="form-error" role="alert" style={{ margin: '4px 4px 10px' }}>
              {formError || error}
            </div>
          )}
        </form>

        {/* Footer Actions */}
        <div className="card-drawer-footer">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            style={{ flex: 1 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="vault-item-form"
            loading={loading}
            style={{ flex: 2 }}
          >
            {item ? 'Save Changes' : 'Save Password'}
          </Button>
        </div>
      </aside>

      {/* Sensitive Auth Modal if password viewing needs re-verification */}
      <SecurityAuthModal
        open={isAuthOpen}
        action="view"
        onClose={() => setIsAuthOpen(false)}
        onSuccess={() => {
          setIsAuthOpen(false);
          setShowPassword(true);
        }}
      />
    </div>
  );
};
